import type { GeneralProductionLine, GeneralProductionResponse, GeneralProductionRow, GeneralProductionUnit } from "../../../shared/general-production";

export type GeneralProductionFilters = { line: string; date: string; unit: string; search: string };
export const emptyGeneralProductionFilters: GeneralProductionFilters = { line: "ALL", date: "ALL", unit: "ALL", search: "" };

export function filterGeneralProductionRows(rows: GeneralProductionRow[], filters: GeneralProductionFilters) {
  return rows.filter((row) =>
    (filters.line === "ALL" || row.lineKey === filters.line)
    && (filters.date === "ALL" || row.date === filters.date)
    && (filters.unit === "ALL" || (filters.unit === "UNKNOWN" ? row.unit === null : row.unit === filters.unit))
    && (!filters.search.trim() || [row.workOrderNo, row.productCode, row.productName, row.lineName]
      .some((value) => value?.toLocaleUpperCase("tr-TR").includes(filters.search.trim().toLocaleUpperCase("tr-TR")))));
}

export function summarizeGeneralProduction(rows: GeneralProductionRow[], unit: GeneralProductionUnit, comparableTarget = true) {
  const planned = rows.filter((row) => row.kind === "PLAN" && row.unit === unit);
  const unmatched = rows.filter((row) => row.kind === "UNMATCHED_ACTUAL" && row.unit === unit);
  const knownActual = planned.filter((row) => row.actualQuantity !== null);
  const fullyKnown = planned.filter((row) => row.actualQuantity !== null
    && row.actualState === "OBSERVED" && row.issueCodes.length === 0);
  const unresolved = planned.filter((row) => row.actualState === "UNRESOLVED" || row.issueCodes.length > 0).length;
  return {
    target: planned.reduce((sum, row) => sum + row.targetQuantity, 0),
    matchedActual: knownActual.length
      ? knownActual.reduce((sum, row) => sum + (row.actualQuantity ?? 0), 0)
      : null,
    actualCoverage: knownActual.length === 0 ? "NONE" as const
      : fullyKnown.length === planned.length ? "COMPLETE" as const : "PARTIAL" as const,
    unmatchedActual: unmatched.reduce((sum, row) => sum + (row.actualQuantity ?? 0), 0),
    remaining: !comparableTarget || unresolved ? null : planned.reduce((sum, row) => sum + (row.remainingQuantity ?? (row.actualState === "FUTURE" ? row.targetQuantity : 0)), 0),
    unresolved,
  };
}

export function generalProductionActualBreakdown(rows: GeneralProductionRow[], unit: GeneralProductionUnit, date: string | null) {
  const total = { PLAN_MATCHED: 0, UNPLANNED: 0, REVIEW: 0 } as const;
  const values: Record<keyof typeof total, number> = { ...total };
  for (const row of rows) {
    if (row.unit !== unit) continue;
    const quantity = row.actualEntries.filter((entry) => !date || entry.date === date).reduce((sum, entry) => sum + entry.quantity, 0);
    values[row.actualClassification] += quantity;
  }
  return { ...values, total: values.PLAN_MATCHED + values.UNPLANNED + values.REVIEW };
}

export function generalProductionUnits(data: GeneralProductionResponse | null, rows: GeneralProductionRow[]) {
  return data ? (["kg", "koli", "adet"] as const).filter((unit) => rows.some((row) => row.unit === unit)) : [];
}

export function generalProductionDays(from: string | null, to: string | null, extra: string[] = []) {
  const days = new Set(extra);
  if (from && to) {
    for (let date = from; date <= to;) {
      days.add(date);
      const next = new Date(`${date}T12:00:00Z`);
      next.setUTCDate(next.getUTCDate() + 1);
      date = next.toISOString().slice(0, 10);
    }
  }
  return [...days].sort();
}

export const generalProductionSourceKey = (row: GeneralProductionRow) => row.kind === "PLAN"
  ? JSON.stringify([row.sourceRow, row.lineKey, row.workOrderNo, row.productCode, row.unit]) : row.id;

export type GeneralProductionTableRow = {
  row: GeneralProductionRow;
  productionOnly: boolean;
  dailyActual: number | null;
  week: ReturnType<typeof summarizeGeneralProduction>;
};

/** Build a display projection; source targets and actual entry dates stay unchanged. */
export function buildGeneralProductionTableRows(rows: GeneralProductionRow[], actualTo: string | null, comparable = true) {
  const sources = new Map<string, GeneralProductionRow[]>();
  for (const row of rows) {
    const key = generalProductionSourceKey(row);
    sources.set(key, [...(sources.get(key) ?? []), row]);
  }
  const result: GeneralProductionTableRow[] = [];
  for (const sourceRows of sources.values()) {
    const template = sourceRows[0];
    if (!template) continue;
    const week = template.unit ? summarizeGeneralProduction(sourceRows, template.unit, comparable) : {
      target: sourceRows.reduce((sum, row) => sum + row.targetQuantity, 0), matchedActual: null,
      actualCoverage: "NONE" as const, unmatchedActual: 0, remaining: null, unresolved: sourceRows.length,
    };
    const actualEntries = sourceRows.flatMap((row) => row.actualEntries);
    const dates = new Set(sourceRows.map((row) => row.date));
    const extraDates = [...new Set(actualEntries.map((entry) => entry.date))].filter((date) => !dates.has(date));
    const displayRows = [...sourceRows.map((row) => ({ row, productionOnly: false })),
      ...extraDates.map((date) => ({ row: { ...template, id: `${template.id}:production:${date}`, date, targetQuantity: 0 }, productionOnly: true }))];
    const countedDays = new Set<string>();
    for (const item of displayRows) {
      const entries = actualEntries.filter((entry) => entry.date === item.row.date);
      const known = actualTo !== null && item.row.date <= actualTo && item.row.unit !== null;
      const comparableSource = sourceRows.every((row) => row.issueCodes.length === 0);
      const dailyActual = countedDays.has(item.row.date) ? null
        : entries.length ? entries.reduce((sum, entry) => sum + entry.quantity, 0)
          : known && comparableSource ? 0 : null;
      countedDays.add(item.row.date);
      result.push({ ...item, dailyActual, week });
    }
  }
  return result.sort(compareGeneralProductionTableRows);
}

export function generalProductionProgress(rows: GeneralProductionRow[], unit: GeneralProductionUnit, date: string | null,
  actualTo: string | null, complete: boolean, comparable = true) {
  const eligible = rows.filter((row) => row.unit === unit);
  const planned = eligible.filter((row) => row.kind === "PLAN" && (!date || row.date === date));
  const target = planned.reduce((sum, row) => sum + row.targetQuantity, 0);
  const entries = eligible.flatMap((row) => row.actualEntries).filter((entry) => !date || entry.date === date);
  const observed = actualTo !== null && (!date || date <= actualTo);
  const uncertain = planned.some((row) => row.issueCodes.length > 0 || row.actualState === "UNRESOLVED");
  const actual = entries.length ? entries.reduce((sum, entry) => sum + entry.quantity, 0) : observed && complete && !uncertain ? 0 : null;
  const fulfilled = !comparable || !complete || uncertain ? null
    : planned.reduce((sum, row) => sum + Math.min(row.targetQuantity, row.actualQuantity ?? 0), 0);
  return { target, actual, fulfilled, remaining: fulfilled === null ? null : Math.max(0, target - fulfilled),
    percentage: fulfilled === null || target <= 0 ? null : Math.round(fulfilled / target * 100), partial: !complete || uncertain };
}

export function compareGeneralProductionTableRows(left: GeneralProductionTableRow, right: GeneralProductionTableRow) {
  const rank = (item: GeneralProductionTableRow) => item.row.kind !== "PLAN" ? 2 : item.row.sourceRow === null ? 1 : 0;
  const ranked = rank(left) - rank(right);
  if (ranked) return ranked;
  if (left.row.kind !== "PLAN" || right.row.kind !== "PLAN" || left.row.sourceRow === null || right.row.sourceRow === null) return 0;
  return left.row.sourceRow - right.row.sourceRow
    || Number(left.productionOnly) - Number(right.productionOnly)
    || left.row.date.localeCompare(right.row.date);
}

/** Contiguous date headings preserve the primary Excel order, even when dates repeat. */
export function generalProductionDayGroups(rows: GeneralProductionTableRow[]) {
  const groups: Array<{ key: string; date: string; items: GeneralProductionTableRow[] }> = [];
  for (const item of [...rows].sort(compareGeneralProductionTableRows)) {
    const last = groups.at(-1);
    if (last?.date === item.row.date) last.items.push(item);
    else groups.push({ key: `${groups.length}:${item.row.date}`, date: item.row.date, items: [item] });
  }
  return groups;
}

export function generalProductionLineOrder(lines: GeneralProductionLine[], rows: GeneralProductionRow[]) {
  const order = new Map<string | null, number>();
  for (const row of rows) if (row.kind === "PLAN" && row.sourceRow !== null) {
    order.set(row.lineKey, Math.min(order.get(row.lineKey) ?? row.sourceRow, row.sourceRow));
  }
  return { planned: lines.filter((line) => order.has(line.key)).sort((a, b) => (order.get(a.key) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.key) ?? Number.MAX_SAFE_INTEGER)),
    other: lines.filter((line) => !order.has(line.key)), order };
}

export function generalProductionSections(rows: GeneralProductionTableRow[], orderRows: GeneralProductionRow[] = rows.map((item) => item.row)) {
  type Kind = "PLAN" | "UNSEQUENCED_PLAN" | "UNMATCHED_ACTUAL";
  const sections = new Map<string, { key: string; lineKey: string | null; kind: Kind; name: string; items: GeneralProductionTableRow[]; sourceOrder: number }>();
  const { order } = generalProductionLineOrder([], orderRows);
  for (const item of [...rows].sort(compareGeneralProductionTableRows)) {
    const kind: Kind = item.row.kind === "UNMATCHED_ACTUAL" ? "UNMATCHED_ACTUAL" : item.row.sourceRow === null ? "UNSEQUENCED_PLAN" : "PLAN";
    const key = JSON.stringify([kind, item.row.lineKey]);
    const section = sections.get(key) ?? { key, lineKey: item.row.lineKey, kind,
      name: item.row.lineKey === null ? "Hat eşleşmesi doğrulanamadı" : item.row.lineName ?? "Hat adı belirtilmemiş", items: [],
      sourceOrder: order.get(item.row.lineKey) ?? item.row.sourceRow ?? Number.MAX_SAFE_INTEGER };
    section.items.push(item);
    sections.set(key, section);
  }
  const rank = { PLAN: 0, UNSEQUENCED_PLAN: 1, UNMATCHED_ACTUAL: 2 };
  return [...sections.values()].sort((a, b) => rank[a.kind] - rank[b.kind] || (a.kind === "PLAN" ? a.sourceOrder - b.sourceOrder : 0));
}

export function generalProductionLineLabel(line: GeneralProductionLine, lines: GeneralProductionLine[]) {
  const sameName = lines.filter((item) => item.name.trim().toLocaleUpperCase("tr-TR") === line.name.trim().toLocaleUpperCase("tr-TR")).length > 1;
  return `${line.name}${sameName ? ` · ${line.id ? "Katalog" : "Excel adı"}` : ""}${line.hasPlan ? "" : " · Plan satırıyla eşleşmedi"}`;
}
